import type { Row, RowData, Table } from '@tanstack/react-table';
import * as React from 'react';

import { DataGridCell } from './DataGridCell';

interface DataGridTableBodyProps<T> {
  table: Table<T>;
  emptyState?: React.ReactNode;
  rowClassName?: (row: Row<T>) => string;
  cellClassName?: string;
  // Triggered from onMouseOver
  onOverRow?: (row: Row<T>) => void;
  // Triggered from onMouseOut
  onOutRow?: (row: Row<T>) => void;
  rowExpanderMode?: 'cell' | 'row';
  expandedRowRenderer?: (row: Row<T>) => React.ReactNode;
}

export function DataGridTableBody<T extends RowData>({
  table,
  emptyState,
  cellClassName,
  rowClassName,
  onOverRow,
  onOutRow,
  rowExpanderMode,
  expandedRowRenderer,
}: DataGridTableBodyProps<T>) {
  return (
    <tbody className="divide-y divide-gray-200 bg-white">
      {table.getRowModel().rows.map((row) => (
        <React.Fragment key={row.id}>
          <tr
            onClick={
              row.getCanExpand() ? row.getToggleExpandedHandler() : () => {}
            }
            key={row.id}
            className={rowClassName && rowClassName(row)}
            onMouseOver={() => onOverRow?.(row)}
            onMouseOut={() => onOutRow?.(row)}
            data-testid={row.id}
          >
            {row.getVisibleCells().map((cell) => (
              <DataGridCell
                cellClassName={cellClassName}
                key={cell.id}
                cell={cell}
              />
            ))}
          </tr>
          {rowExpanderMode === 'row' &&
          row.getCanExpand() &&
          row.getIsExpanded() &&
          expandedRowRenderer ? (
            <tr key={`${row.id}-expanded`} data-testid={`${row.id}-expanded`}>
              <td>{/* expander slot */}</td>
              <td colSpan={row.getVisibleCells().length - 1}>
                {expandedRowRenderer(row)}
              </td>
            </tr>
          ) : null}
        </React.Fragment>
      ))}
      {table.getRowModel().rows.length === 0 && (
        <tr>
          <td colSpan={table.getAllColumns().length}>{emptyState}</td>
        </tr>
      )}
    </tbody>
  );
}
